/**
 * EnquiryConfirmationScreen
 * ─────────────────────────
 * Shared confirmation block shown after a successful public enquiry submission
 * on both the Priya Concierge widget and the Plan Your Event (SmartStudio) journey.
 *
 * Props
 * ─────
 * publicReference  {string}   PRE-xxxxxxxx reference returned by the backend.
 * whatsappUrl      {string=}  Pre-built wa.me click-to-chat URL (optional).
 *                             When omitted, the WhatsApp button is not rendered.
 * onWhatsAppClick  {fn=}      Called before the WhatsApp link opens (analytics).
 * onRestart        {fn=}      Called when the user clicks "Start a new enquiry".
 * testIdPrefix     {string=}  data-testid prefix ("concierge" | "ss").
 */

import { useState } from "react";
import { Check, Copy, CheckCheck, MessageCircle, ExternalLink, RefreshCw } from "lucide-react";

export default function EnquiryConfirmationScreen({
  publicReference,
  whatsappUrl,
  onWhatsAppClick,
  onRestart,
  testIdPrefix = "enq-confirm",
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!publicReference) return;
    navigator.clipboard.writeText(publicReference).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <div
      data-testid={`${testIdPrefix}-submitted`}
      style={{ padding: "16px 0" }}
    >
      {/* Icon */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "#f0fdf4",
            border: "2px solid #bbf7d0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 14px",
          }}
        >
          <Check size={28} color="#16a34a" strokeWidth={2.5} />
        </div>

        {/* Headline */}
        <h3
          style={{
            fontWeight: "700",
            fontSize: "20px",
            color: "#1a1208",
            margin: "0 0 4px",
            letterSpacing: "-0.01em",
          }}
        >
          Enquiry Received!
        </h3>
        <p
          style={{
            fontSize: "13px",
            color: "#7a6a55",
            margin: "0 0 16px",
          }}
        >
          Thank you for choosing Prashasta Events.
        </p>
      </div>

      {/* Reference block */}
      {publicReference && (
        <div
          style={{
            background: "#fdf8f0",
            border: "1.5px solid #e8d9be",
            borderRadius: "12px",
            padding: "14px 16px",
            marginBottom: "16px",
          }}
        >
          <p
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#b8940a",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              margin: "0 0 6px",
            }}
          >
            Your Enquiry Reference
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              data-testid={`${testIdPrefix}-public-ref`}
              style={{
                fontFamily: "monospace",
                fontSize: "16px",
                fontWeight: "700",
                color: "#1a1208",
                letterSpacing: "0.03em",
                flex: 1,
              }}
            >
              {publicReference}
            </span>
            <button
              data-testid={`${testIdPrefix}-copy-ref`}
              onClick={handleCopy}
              title="Copy reference"
              style={{
                background: copied ? "#f0fdf4" : "#fff",
                border: `1.5px solid ${copied ? "#bbf7d0" : "#e8d9be"}`,
                borderRadius: "8px",
                padding: "4px 10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "12px",
                color: copied ? "#16a34a" : "#9a7133",
                transition: "all 0.2s",
              }}
            >
              {copied ? <CheckCheck size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Assignment status */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          background: "#fffbf0",
          border: "1.5px solid #fde68a",
          borderRadius: "10px",
          padding: "12px 14px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: "#f59e0b",
            flexShrink: 0,
            boxShadow: "0 0 0 3px #fde68a",
          }}
        />
        <div>
          <p
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "#92400e",
              margin: "0 0 2px",
            }}
          >
            Assignment in progress
          </p>
          <p style={{ fontSize: "12px", color: "#78716c", margin: 0, lineHeight: "1.4" }}>
            Our team is reviewing your enquiry. You'll receive a call from your
            dedicated Relationship Manager soon.
          </p>
        </div>
      </div>

      {/* WhatsApp CTA — only rendered when a URL is available */}
      {whatsappUrl && (
        <a
          data-testid={`${testIdPrefix}-whatsapp-cta`}
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onWhatsAppClick}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "12px",
            borderRadius: "12px",
            background: "#25d366",
            color: "#fff",
            textDecoration: "none",
            fontWeight: "600",
            fontSize: "14px",
            marginBottom: "10px",
          }}
        >
          <MessageCircle size={18} />
          Continue on WhatsApp
          <ExternalLink size={14} style={{ opacity: 0.7 }} />
        </a>
      )}

      {/* Restart */}
      {onRestart && (
        <button
          data-testid={`${testIdPrefix}-restart`}
          onClick={onRestart}
          style={{
            width: "100%",
            marginTop: whatsappUrl ? "4px" : "0",
            padding: "10px",
            borderRadius: "10px",
            background: "#f5f0e8",
            border: "none",
            color: "#9a7133",
            cursor: "pointer",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          <RefreshCw size={14} />
          Start a new enquiry
        </button>
      )}
    </div>
  );
}
